import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Minus, AlertTriangle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import axios from 'axios';

import { BACKEND_URL } from '../config';
const API = `${BACKEND_URL}/api`;

const ChatBox = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [showRulesPopup, setShowRulesPopup] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Check if user has seen rules popup (store in localStorage)
  useEffect(() => {
    if (isOpen && !isMinimized) {
      const hasSeenRules = localStorage.getItem(`chatRulesSeen_${user?.username}`);
      if (!hasSeenRules) {
        setShowRulesPopup(true);
      }
    }
  }, [isOpen, isMinimized, user]);

  const handleAcceptRules = () => {
    localStorage.setItem(`chatRulesSeen_${user?.username}`, 'true');
    setShowRulesPopup(false);
  };

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/chat/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  useEffect(() => {
    if (isOpen && !isMinimized) {
      fetchMessages();
      // Poll for new messages every 3 seconds
      const interval = setInterval(fetchMessages, 3000);
      return () => clearInterval(interval);
    }
  }, [isOpen, isMinimized]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/chat/send`,
        { message: newMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewMessage('');
      fetchMessages();
    } catch (error) {
      console.error('Error sending message:', error);
      alert(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  if (!user) return null;

  return (
    <>
      {/* Chat Button (when closed) */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 bg-[#0d6571] hover:bg-[#0d6571]/90 text-white p-4 rounded-full shadow-lg transition-all duration-300 hover:scale-110"
          title="Open Chat"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className={`fixed right-6 z-50 bg-white rounded-lg shadow-2xl transition-all duration-300 ${
          isMinimized ? 'bottom-6' : 'bottom-6'
        }`}>
          {/* Chat Header */}
          <div className="bg-[#0d6571] text-white px-4 py-3 rounded-t-lg flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5" />
              <h3 className="font-semibold">Member Chat</h3>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsMinimized(!isMinimized)}
                className="hover:bg-white/20 p-1 rounded transition-colors"
                title={isMinimized ? 'Maximize' : 'Minimize'}
              >
                <Minus className="w-4 h-4" />
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="hover:bg-white/20 p-1 rounded transition-colors"
                title="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Chat Body */}
          {!isMinimized && (
            <>
              {/* Rules Popup Overlay */}
              {showRulesPopup && (
                <div className="absolute inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 rounded-lg">
                  <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in duration-200">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="p-3 bg-red-100 rounded-lg flex-shrink-0">
                        <AlertTriangle className="w-8 h-8 text-red-600" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">
                          Welcome to Member Chat
                        </h3>
                        <p className="text-gray-700 leading-relaxed">
                          Please follow the{' '}
                          <Link 
                            to="/rules" 
                            className="text-[#0d6571] font-semibold hover:text-[#d97841] underline"
                            onClick={() => setIsOpen(false)}
                          >
                            Rules
                          </Link>
                          , there is a <strong>zero-tolerance policy</strong>.
                        </p>
                      </div>
                    </div>
                    
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                      <p className="text-sm text-red-800 font-medium">
                        Violations may result in warnings, suspension, or permanent ban from the chatbox.
                      </p>
                    </div>
                    
                    <div className="flex gap-3">
                      <Link
                        to="/rules"
                        onClick={() => setIsOpen(false)}
                        className="flex-1 px-4 py-2 border border-[#0d6571] text-[#0d6571] rounded-lg hover:bg-[#0d6571]/5 font-semibold text-center transition-colors"
                      >
                        Read Full Rules
                      </Link>
                      <button
                        onClick={handleAcceptRules}
                        className="flex-1 px-4 py-2 bg-[#0d6571] text-white rounded-lg hover:bg-[#0d6571]/90 font-semibold transition-colors"
                      >
                        I Understand
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="w-80 h-96 overflow-y-auto p-4 space-y-3 bg-gray-50">
                {messages.length === 0 ? (
                  <div className="text-center text-gray-500 mt-20">
                    <MessageCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No messages yet. Start the conversation!</p>
                  </div>
                ) : (
                  messages.map((msg, index) => {
                    const isOwnMessage = msg.username === user.username;
                    return (
                      <div
                        key={index}
                        className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[70%] rounded-lg px-3 py-2 ${
                            isOwnMessage
                              ? 'bg-[#0d6571] text-white'
                              : 'bg-white border border-gray-200'
                          }`}
                        >
                          {!isOwnMessage && (
                            <div className="text-xs font-semibold text-[#0d6571] mb-1">
                              {msg.username}
                            </div>
                          )}
                          <div className="text-sm break-words">{msg.message}</div>
                          <div className={`text-xs mt-1 ${
                            isOwnMessage ? 'text-white/70' : 'text-gray-500'
                          }`}>
                            {new Date(msg.timestamp).toLocaleTimeString([], { 
                              hour: '2-digit', 
                              minute: '2-digit' 
                            })}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Chat Input */}
              <form onSubmit={handleSend} className="p-3 border-t border-gray-200 bg-white rounded-b-lg">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    maxLength={500}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0d6571] focus:border-transparent"
                    disabled={sending}
                  />
                  <button
                    type="submit"
                    disabled={!newMessage.trim() || sending}
                    className="bg-[#0d6571] text-white p-2 rounded-lg hover:bg-[#0d6571]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {newMessage.length}/500 characters
                </div>
              </form>
            </>
          )}

          {/* Minimized State */}
          {isMinimized && (
            <div className="w-80 px-4 py-2 text-center text-sm text-gray-600">
              Click to expand
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ChatBox;
